import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Plus, RefreshCw, TriangleAlert, X } from 'lucide-react';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import FilterBar from '../ui/FilterBar';
import useUrlFilters from '../ui/useUrlFilters';
import DataTable, { ColumnsMenu, useColumnVisibility } from '../ui/DataTable';
import ListPage from '../ui/ListPage';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import ConfirmButton from '../ui/ConfirmButton';
import Menu from '../ui/Menu';
import Badge, { StatusBadge } from '../ui/Badge';
import { Spinner } from '../ui/Loading';
import { useToast } from '../ui/Toast';
import { formatCount, formatDateTime, plural, teamLabel } from '../ui/format';
import { ticketPath } from '../ui/entityRoutes';
import usePagedQuery from '../ui/usePagedQuery';
import CreateTicketsPanel from './CreateTicketsPanel';
import {
  MODE_OPTIONS,
  PRIORITY_OPTIONS,
  STATUS_CHANGE_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TEAM_OPTIONS,
  IMAGE_FLOW_OPTION,
  isImageTicket,
  isPreview,
  jotformUrl,
  ticketModeLabel,
  ticketName,
  ticketTypeLabel,
} from './ticketFields';

const FILTER_DEFAULTS = { kw: '', created: '', status: '', priority: '', mode: '', team: '' };
const withAll = (options) => [{ value: '', label: 'All' }, ...options];

const FILTERS = [
  { key: 'kw', type: 'search', label: 'Search', placeholder: 'Ticket name or assignee', width: 280 },
  { key: 'created', type: 'date', label: 'Created' },
  { key: 'status', type: 'select', label: 'Status', options: STATUS_FILTER_OPTIONS },
  { key: 'priority', type: 'select', label: 'Priority', options: withAll(PRIORITY_OPTIONS) },
  { key: 'mode', type: 'select', label: 'Mode', options: withAll(MODE_OPTIONS) },
  { key: 'team', type: 'select', label: 'Team', options: withAll([...TEAM_OPTIONS, IMAGE_FLOW_OPTION]) },
];

const muted = (text) => <span className="text-graphite">{text}</span>;
const dash = (value) => value || muted('—');
const countCell = (n) => (n ? formatCount(n) : muted('0'));
const dateCell = (value) => <span className="text-graphite tabular-nums">{formatDateTime(value) || '—'}</span>;

function StatusCell({ ticket, updating, onChange }) {
  if (isPreview(ticket)) {
    return <StatusBadge status={ticket.status} />;
  }
  if (updating) {
    return (
      <span className="inline-flex items-center gap-2 h-7">
        <StatusBadge status={ticket.status} />
        <Spinner size={13} />
      </span>
    );
  }
  const current = String(ticket.status || '').toLowerCase();
  const items = STATUS_CHANGE_OPTIONS.map((option) => ({
    key: option.value,
    label: option.label,
    type: 'radio',
    checked: option.value.toLowerCase() === current,
    onSelect: () => option.value.toLowerCase() !== current && onChange(ticket, option),
  }));
  return (
    <Menu items={items} label={`Change status of ${ticketName(ticket)}`} variant="ghost" size="sm" triggerClassName="-ml-1.5 !px-1.5 gap-1">
      <StatusBadge status={ticket.status} />
      <ChevronDown size={13} className="text-graphite" aria-hidden="true" />
    </Menu>
  );
}

const columns = ({ updatingId, onStatusChange }) => [
  {
    key: 'name',
    header: 'Ticket',
    hideable: false,
    grow: true,
    minWidth: 220,
    fullText: ticketName,
    render: (t) => (
      <Link to={ticketPath(t.id)} className="font-mono text-id text-signal hover:underline">
        {ticketName(t)}
      </Link>
    ),
  },
  { key: 'type', header: 'Type', render: (t) => dash(ticketTypeLabel(t.ticket_type)) },
  { key: 'team', header: 'Team', render: (t) => dash(teamLabel(t.flow_type)) },
  { key: 'status', header: 'Status', render: (t) => <StatusCell ticket={t} updating={updatingId === t.id} onChange={onStatusChange} /> },
  { key: 'mode', header: 'Mode', render: (t) => (isPreview(t) ? <Badge tone="neutral">Preview</Badge> : dash(ticketModeLabel(t.mode))) },
  { key: 'priority', header: 'Priority', render: (t) => (t.priority === 'Urgent' ? <span className="font-semibold">Urgent</span> : dash(t.priority)) },
  { key: 'assignee', header: 'Assignee', maxWidth: 160, fullText: (t) => t.assignee, render: (t) => dash(t.assignee) },
  { key: 'hosts', header: 'Hosts', align: 'right', render: (t) => (isImageTicket(t) ? muted('—') : countCell(t.host_count)) },
  { key: 'packages', header: 'Packages', align: 'right', defaultHidden: true, render: (t) => countCell(t.package_count) },
  { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (t) => countCell(t.vuln_count) },
  { key: 'created', header: 'Created', render: (t) => dateCell(t.created_at) },
  { key: 'updated', header: 'Last update', defaultHidden: true, render: (t) => dateCell(t.updated_at) },
  { key: 'synced', header: 'Last synced', defaultHidden: true, render: (t) => dateCell(t.synced_at) },
];
// Column definitions for the visibility state and the Columns menu; rendering uses columns() with live state.
const COLUMN_LIST = columns({});

function SkippedHostsNotice({ hosts, onDismiss }) {
  const shown = hosts.slice(0, 6);
  const rest = hosts.length - shown.length;
  return (
    <div role="status" className="shrink-0 mb-3 flex items-start gap-2.5 pl-3 pr-1.5 py-2 rounded-panel border border-med/40 bg-med-tint text-body text-ink">
      <TriangleAlert size={16} strokeWidth={2} className="shrink-0 mt-0.5 text-med" aria-hidden="true" />
      <p className="flex-1 min-w-0">
        {plural(hosts.length, 'host')} got no ticket because {hosts.length === 1 ? 'it has' : 'they have'} no host admin:{' '}
        <span className="font-mono text-id">{shown.join(', ')}</span>
        {rest > 0 && <span title={hosts.slice(shown.length).join(', ')}> and {rest} more</span>}
        . Set an admin in{' '}
        <Link to="/host-ownership" className="text-signal hover:underline">Host Ownership</Link>
        , then create the tickets again.
      </p>
      <button type="button" onClick={onDismiss} className="shrink-0 p-1 rounded-control text-graphite hover:bg-hover hover:text-ink" aria-label="Dismiss notice">
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

// Toast text for the bulk "Open in Jotform" result, as the legacy page reported it.
function reportPromotion(result, toast) {
  const processed = result?.processed ?? 0;
  const succeeded = result?.succeeded ?? 0;
  const failed = Array.isArray(result?.failed) ? result.failed : [];
  const records = Array.isArray(result?.records) ? result.records : [];
  const canceled = records.filter((r) => String(r?.status || '').toLowerCase() === 'canceled').length;

  if (succeeded > 0) toast.success(`Opened ${succeeded} of ${processed} ${processed === 1 ? 'ticket' : 'tickets'} in Jotform.`);
  const problems = [];
  if (failed.length > 0) problems.push(`Tickets ${failed.map((f) => f.ticket_id).filter(Boolean).join(', ')} could not be updated.`);
  if (canceled > 0) problems.push(`${plural(canceled, 'ticket')} canceled because Jotform creation failed. Check JOTFORM_API_KEY on the API server.`);
  if (problems.length > 0) toast.error(problems.join(' '));
  else if (succeeded === 0) toast.error(processed > 0 ? 'No tickets were opened in Jotform.' : 'No tickets were processed.');
}

export default function Tickets() {
  const toast = useToast();
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);
  const columnState = useColumnVisibility(COLUMN_LIST, 'sbom-columns-tickets');
  const [panelOpen, setPanelOpen] = useState(false);
  const [skippedHosts, setSkippedHosts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [updatingId, setUpdatingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const key = JSON.stringify(filters);
  const fetchPage = useCallback(
    (paging) => API.GET_TICKETS(null, {
      kw: filters.kw,
      created_on: filters.created ? filters.created.split('-').reverse().join('_') : undefined,
      status: filters.status,
      priority: filters.priority,
      mode: filters.mode,
      flow_type: filters.team,
      ...paging,
    }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = usePagedQuery(fetchPage, key);
  const page = query.pagination.page;
  const { rows, retry } = query;

  useEffect(() => setSelected([]), [key, page]);

  const changeStatus = async (ticket, option) => {
    setUpdatingId(ticket.id);
    try {
      await API.UPDATE_TICKET_STATUS(ticket.id, option.value);
      toast.success(`${ticketName(ticket)} is now ${option.label.toLowerCase()}.`);
      retry();
    } catch (error) {
      toast.error(`The status of ${ticketName(ticket)} could not be changed: ${error.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const syncAll = async () => {
    setSyncing(true);
    try {
      const summary = await API.SYNC_TICKETS();
      const parts = [];
      if (summary.synced > 0) parts.push(`${summary.synced} synced`);
      if (summary.changed > 0) parts.push(`${summary.changed} updated`);
      if (summary.errors > 0) parts.push(`${summary.errors} failed`);
      const msg = parts.length > 0 ? parts.join(', ') + '.' : 'No tickets to sync.';
      if (summary.errors > 0) toast.error(`Sync finished: ${msg}`);
      else toast.success(`Sync finished: ${msg}`);
      retry();
    } catch (error) {
      toast.error(`Sync failed: ${error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const selectedTickets = rows.filter((t) => selected.includes(t.id));
  const previewIds = selectedTickets.filter(isPreview).map((t) => t.id);
  const keptCount = selectedTickets.length - previewIds.length;

  const openInJotform = async () => {
    setBulkBusy('open');
    try {
      reportPromotion(await API.CREATE_TICKETS_FROM_PREVIEWS(selected), toast);
      setSelected([]);
      retry();
    } catch (error) {
      toast.error(`Tickets could not be opened in Jotform: ${error.message}`);
    } finally {
      setBulkBusy(null);
    }
  };

  const deleteSelected = async () => {
    setBulkBusy('delete');
    const results = await Promise.allSettled(previewIds.map((id) => API.DELETE_TICKET(id)));
    const failed = results.filter((r) => r.status === 'rejected');
    const deleted = previewIds.length - failed.length;
    if (deleted > 0) toast.success(`Deleted ${plural(deleted, 'preview ticket')}.${keptCount > 0 ? ` ${plural(keptCount, 'ticket')} opened in Jotform ${keptCount === 1 ? 'was' : 'were'} kept.` : ''}`);
    if (failed.length > 0) toast.error(`${plural(failed.length, 'ticket')} could not be deleted: ${failed[0].reason?.message || 'Delete failed'}`);
    setBulkBusy(null);
    setSelected([]);
    retry();
  };

  const selectionBar = (
    <>
      <span className="font-semibold tabular-nums">{formatCount(selected.length)} selected</span>
      <span className="mx-1 h-4 border-l border-rule" aria-hidden="true" />
      {bulkBusy !== 'delete' && (
        <Button size="sm" onClick={openInJotform} disabled={bulkBusy === 'open'}>
          {bulkBusy === 'open' ? 'Opening in Jotform…' : 'Open in Jotform'}
        </Button>
      )}
      {bulkBusy !== 'open' && (
        <ConfirmButton
          size="sm"
          disabled={previewIds.length === 0}
          title={previewIds.length === 0 ? 'Only preview tickets can be deleted' : undefined}
          prompt={`Delete ${plural(previewIds.length, 'preview ticket')}?${keptCount > 0 ? ` ${formatCount(keptCount)} opened in Jotform will be kept.` : ''}`}
          confirmLabel="Delete"
          busyLabel="Deleting…"
          busy={bulkBusy === 'delete'}
          onConfirm={deleteSelected}
        >
          Delete
        </ConfirmButton>
      )}
      <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setSelected([])} disabled={Boolean(bulkBusy)}>
        Clear selection
      </Button>
    </>
  );

  const filtered = FILTERS.some((f) => filters[f.key] !== FILTER_DEFAULTS[f.key]);
  const empty = filtered ? (
    <EmptyState title="No tickets match these filters" action={<Button onClick={() => setFilters(FILTER_DEFAULTS)}>Clear filters</Button>}>
      Search matches part of the ticket name or the assignee. Widen the other filters, or clear them.
    </EmptyState>
  ) : (
    <EmptyState title="No tickets yet" action={<Button variant="primary" onClick={() => setPanelOpen(true)}>Create tickets</Button>}>
      Tickets collect vulnerable packages for the people who fix them. Create them in preview mode to review them before they go to Jotform.
    </EmptyState>
  );

  const tableColumns = columns({ updatingId, onStatusChange: changeStatus });

  return (
    <ListPage>
      <PageHeader
        title="Tickets"
        actions={(
          <>
            <Button onClick={syncAll} disabled={syncing}>
              <RefreshCw size={15} strokeWidth={2} className={syncing ? 'animate-spin' : ''} aria-hidden="true" />
              {syncing ? 'Syncing…' : 'Sync with Jotform'}
            </Button>
            <ColumnsMenu columns={COLUMN_LIST} hidden={columnState.hidden} onToggle={columnState.toggle} onReset={columnState.reset} />
            <Button variant="primary" onClick={() => setPanelOpen(true)}>
              <Plus size={15} strokeWidth={2} aria-hidden="true" />
              Create tickets
            </Button>
          </>
        )}
      />
      <FilterBar className="mb-3" filters={FILTERS} values={filters} onChange={setFilters} />
      {skippedHosts.length > 0 && <SkippedHostsNotice hosts={skippedHosts} onDismiss={() => setSkippedHosts([])} />}
      <DataTable
        className="flex-1"
        label="Tickets"
        columns={tableColumns}
        hidden={columnState.hidden}
        rows={rows}
        rowKey={(t) => t.id}
        rowLabel={ticketName}
        rowMenu={(t) => [
          { key: 'open', label: 'Open ticket', to: ticketPath(t.id) },
          ...(jotformUrl(t) ? [{ key: 'jotform', label: 'Show in Jotform', href: jotformUrl(t) }] : []),
        ]}
        selection={{ selected, onChange: setSelected }}
        selectionBar={selectionBar}
        loading={query.loading}
        error={query.error}
        errorTitle="Tickets could not be loaded"
        onRetry={retry}
        empty={empty}
        pagination={query.pagination}
      />
      <CreateTicketsPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onCreated={(summary) => {
          setSkippedHosts(summary.skippedHosts);
          retry();
        }}
      />
    </ListPage>
  );
}
